// LocationPicker — choose a node in the sites hierarchy (org→site→building→
// floor→zone) to place/move a device. Cascading selects; the deepest selected
// level becomes the placement target. Used by the "Place / Move…" action.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import {
  sitesApi, hierarchyApi,
  type PlacementTarget, type Site, type Building, type Floor, type Zone,
} from '@/lib/inventory-api';

export function LocationPicker({
  title,
  onClose, onPlace,
}: {
  title?: string;
  onClose: () => void;
  onPlace: (target: PlacementTarget) => void;
}) {
  const { t } = useTranslation('components');
  const { data: sites } = useQuery({ queryKey: ['sites'], queryFn: sitesApi.list, retry: false });
  const [siteId, setSiteId] = useState('');
  const [buildingId, setBuildingId] = useState('');
  const [floorId, setFloorId] = useState('');
  const [zoneId, setZoneId] = useState('');

  const { data: buildings } = useQuery<Building[]>({
    queryKey: ['buildings', siteId], queryFn: () => hierarchyApi.buildings(siteId), enabled: !!siteId, retry: false,
  });
  const { data: floors } = useQuery<Floor[]>({
    queryKey: ['floors', buildingId], queryFn: () => hierarchyApi.floors(buildingId), enabled: !!buildingId, retry: false,
  });
  const { data: zones } = useQuery<Zone[]>({
    queryKey: ['zones', floorId], queryFn: () => hierarchyApi.zones(floorId), enabled: !!floorId, retry: false,
  });

  useEffect(() => { setBuildingId(''); setFloorId(''); setZoneId(''); }, [siteId]);
  useEffect(() => { setFloorId(''); setZoneId(''); }, [buildingId]);
  useEffect(() => { setZoneId(''); }, [floorId]);

  const sel = 'w-full rounded-md bg-white/5 px-3 py-2 text-sm ring-1 ring-white/10 focus:outline-none focus:ring-primary/50';

  const confirm = () => {
    const target: PlacementTarget = zoneId ? { zoneId }
      : floorId ? { floorId } : buildingId ? { buildingId } : siteId ? { siteId } : {};
    onPlace(target);
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div className="tk-modal w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{title ?? t('locationPicker.title')}</h2>
          <button className="tk-btn-ghost px-2" onClick={onClose} aria-label={t('locationPicker.actions.close')}><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm text-muted">{t('locationPicker.fields.site')}</label>
            <select className={sel} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">{t('locationPicker.placeholders.selectSite')}</option>
              {(sites ?? []).map((s: Site) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          {siteId && (
            <div>
              <label className="mb-1 block text-sm text-muted">{t('locationPicker.fields.building')}</label>
              <select className={sel} value={buildingId} onChange={(e) => setBuildingId(e.target.value)}>
                <option value="">{t('locationPicker.placeholders.siteLevel')}</option>
                {(buildings ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          {buildingId && (
            <div>
              <label className="mb-1 block text-sm text-muted">{t('locationPicker.fields.floor')}</label>
              <select className={sel} value={floorId} onChange={(e) => setFloorId(e.target.value)}>
                <option value="">{t('locationPicker.placeholders.buildingLevel')}</option>
                {(floors ?? []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
          )}
          {floorId && (
            <div>
              <label className="mb-1 block text-sm text-muted">{t('locationPicker.fields.zone')}</label>
              <select className={sel} value={zoneId} onChange={(e) => setZoneId(e.target.value)}>
                <option value="">{t('locationPicker.placeholders.floorLevel')}</option>
                {(zones ?? []).map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
              </select>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button className="tk-btn-ghost" onClick={onClose}>{t('locationPicker.actions.cancel')}</button>
            <button className="tk-btn-primary" onClick={confirm} disabled={!siteId}>{t('locationPicker.actions.placeHere')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
